/**
 * POS Terminalleri Ayarlar Sayfası (Görev 17 — Kasa Doğrulama)
 *
 * Tek sayfa, tablo-driven CRUD ekranı. Anayasa Madde 1/2/3/5/6 ile uyumlu:
 *  - Madde 1: Lokasyon dropdown'ı `/api/lokasyonlar`'tan beslenir
 *  - Madde 2: Listele + Ekle + Düzenle + Pasifleştir/Aktifleştir + Sil (5 op)
 *  - Madde 3: `kod` immutable — Düzenle modal'ında disabled+readOnly
 *  - Madde 5: Backend MANAGE_SETTINGS enforce eder, frontend UX gating de yapar
 *  - Madde 6: Default soft delete, hard delete `?hard=true` + `usage_count == 0`
 *
 * Backend endpoint'leri (G3 PR #111):
 *  - GET    /api/pos-terminalleri?include_inactive=&with_usage=
 *  - POST   /api/pos-terminalleri
 *  - PATCH  /api/pos-terminalleri/{id}
 *  - DELETE /api/pos-terminalleri/{id}        → soft (aktif=false)
 *  - DELETE /api/pos-terminalleri/{id}?hard=true → hard (usage=0 koşulu)
 */

import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { styles, colors, radius } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EMPTY_FORM = {
  kod: '',
  ad: '',
  banka: '',
  lokasyon_id: '',
  son_dort_hane: '',
  sira: 0,
};

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '13px',
  boxSizing: 'border-box',
};

const labelStyle = { fontSize: 12, color: '#666', display: 'block', marginBottom: 4 };

const PosTerminalleriPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = hasPermission(user?.role, PERMISSIONS.MANAGE_SETTINGS, user?.effectivePermissions);

  const [terminaller, setTerminaller] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Yeni terminal modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_FORM });
  const [createError, setCreateError] = useState('');

  // Düzenle modal
  const [editModal, setEditModal] = useState(null); // { id, kod, ad, banka, lokasyon_id, son_dort_hane, sira, aktif }
  const [editError, setEditError] = useState('');

  // ─── Veri yükleme ──────────────────────────────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [terRes, lokRes] = await Promise.all([
        fetch(
          `${API_URL}/api/pos-terminalleri?include_inactive=${includeInactive}&with_usage=true`,
          { headers: authHeaders() },
        ),
        fetch(`${API_URL}/api/lokasyonlar`, { headers: authHeaders() }),
      ]);
      const terData = await terRes.json().catch(() => []);
      const lokData = await lokRes.json().catch(() => []);
      setTerminaller(Array.isArray(terData) ? terData : []);
      setLokasyonlar(Array.isArray(lokData) ? lokData : []);
    } catch (err) {
      logError('POS Terminalleri yüklenemedi:', err);
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  // Lokasyon adı lookup
  const lokasyonMap = useMemo(() => {
    const m = {};
    for (const l of lokasyonlar) m[l.id] = l;
    return m;
  }, [lokasyonlar]);

  const aktifLokasyonlar = useMemo(
    () => lokasyonlar.filter((l) => l.aktif !== false),
    [lokasyonlar],
  );

  // ─── CRUD: Oluştur ─────────────────────────────────────────────
  const openCreate = () => {
    setCreateForm({ ...EMPTY_FORM });
    setCreateError('');
    setCreateModalOpen(true);
  };

  const submitCreate = async () => {
    const { kod, ad, lokasyon_id, son_dort_hane } = createForm;
    if (!kod.trim()) return setCreateError('Kod zorunludur.');
    if (!ad.trim()) return setCreateError('Ad zorunludur.');
    if (!lokasyon_id) return setCreateError('Lokasyon seçiniz.');
    if (son_dort_hane && !/^\d{4}$/.test(son_dort_hane))
      return setCreateError('Son 4 hane tam olarak 4 rakam olmalı.');

    setSaving(true);
    setCreateError('');
    try {
      const payload = {
        kod: kod.trim(),
        ad: ad.trim(),
        banka: createForm.banka.trim() || null,
        lokasyon_id: parseInt(lokasyon_id, 10),
        son_dort_hane: son_dort_hane || null,
        sira: parseInt(createForm.sira, 10) || 0,
      };
      const res = await fetch(`${API_URL}/api/pos-terminalleri`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Terminal eklendi');
      setCreateModalOpen(false);
      fetchAll();
    } catch (err) {
      setCreateError(err.message || 'Eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── CRUD: Düzenle ─────────────────────────────────────────────
  const openEdit = (t) => {
    setEditModal({
      id: t.id,
      kod: t.kod,
      ad: t.ad || '',
      banka: t.banka || '',
      lokasyon_id: t.lokasyon_id || '',
      son_dort_hane: t.son_dort_hane || '',
      sira: t.sira ?? 0,
      aktif: t.aktif !== false,
    });
    setEditError('');
  };

  const submitEdit = async () => {
    if (!editModal) return;
    const { ad, lokasyon_id, son_dort_hane } = editModal;
    if (!ad.trim()) return setEditError('Ad zorunludur.');
    if (!lokasyon_id) return setEditError('Lokasyon seçiniz.');
    if (son_dort_hane && !/^\d{4}$/.test(son_dort_hane))
      return setEditError('Son 4 hane tam olarak 4 rakam olmalı.');

    setSaving(true);
    setEditError('');
    try {
      // NOT: `kod` payload'a EKLENMEZ — Anayasa Madde 3 (immutable).
      const payload = {
        ad: ad.trim(),
        banka: editModal.banka.trim() || null,
        lokasyon_id: parseInt(lokasyon_id, 10),
        son_dort_hane: son_dort_hane || null,
        sira: parseInt(editModal.sira, 10) || 0,
        aktif: editModal.aktif,
      };
      const res = await fetch(`${API_URL}/api/pos-terminalleri/${editModal.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Terminal güncellendi');
      setEditModal(null);
      fetchAll();
    } catch (err) {
      setEditError(err.message || 'Güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Pasifleştir / Aktifleştir ─────────────────────────────────
  const togglePasif = async (t) => {
    if (t.aktif !== false) {
      if (!window.confirm(`"${t.ad}" terminali pasifleştirilsin mi? (geri alınabilir)`)) return;
      try {
        const res = await fetch(`${API_URL}/api/pos-terminalleri/${t.id}`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `HTTP ${res.status}`);
        }
        toast.success('Terminal pasifleştirildi');
        fetchAll();
      } catch (err) {
        toast.error(err.message || 'Pasifleştirilemedi');
      }
    } else {
      // Aktifleştir → PATCH {aktif: true}
      try {
        const res = await fetch(`${API_URL}/api/pos-terminalleri/${t.id}`, {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ aktif: true }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `HTTP ${res.status}`);
        }
        toast.success('Terminal aktifleştirildi');
        fetchAll();
      } catch (err) {
        toast.error(err.message || 'Aktifleştirilemedi');
      }
    }
  };

  // ─── Hard Delete (Sil) ─────────────────────────────────────────
  const hardDelete = async (t) => {
    const usage = t.usage_count || 0;
    if (usage > 0) {
      toast.warning(
        `Bu terminal ${usage} kapanışta kullanılıyor — kalıcı silinemez. Pasifleştirmeyi deneyin.`,
      );
      return;
    }
    if (
      !window.confirm(
        `"${t.ad}" terminali KALICI olarak silinecek. Geri alınamaz. Devam?`,
      )
    )
      return;
    try {
      const res = await fetch(
        `${API_URL}/api/pos-terminalleri/${t.id}?hard=true`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Terminal kalıcı olarak silindi');
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Silinemedi');
    }
  };

  // ─── Yetki yoksa erken çıkış ───────────────────────────────────
  if (!canManage) {
    return (
      <div>
        <Header title="POS Terminalleri" />
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Bu sayfayı görüntüleme yetkiniz yok. (MANAGE_SETTINGS gerekli)
        </div>
      </div>
    );
  }

  if (loading && terminaller.length === 0) {
    return (
      <div>
        <Header title="POS Terminalleri" />
        <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="POS Terminalleri" />

      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {/* Bilgi notu */}
        <div
          style={{
            background: '#eef6ff',
            border: '1px solid #b6daff',
            color: '#0b5394',
            padding: '12px 16px',
            borderRadius: radius.md,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          POS terminalleri kasa doğrulama sayfasında kart kalemleri için kullanılır. Her
          terminal bir lokasyona bağlanır. <strong>Kod</strong> kayıt sonrası
          değiştirilemez (Anayasa Madde 3); yazım hatası olursa pasifleştirip yenisini
          ekleyin.
        </div>

        {/* Üst bar: filtre + yeni butonu */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: colors.textSecondary,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Pasifleri göster
          </label>

          <button
            onClick={openCreate}
            style={{
              padding: '10px 18px',
              background: '#c62828',
              color: 'white',
              border: 'none',
              borderRadius: radius.sm,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(198,40,40,0.25)',
            }}
          >
            + Yeni Terminal
          </button>
        </div>

        {/* Tablo */}
        <div
          style={{
            background: colors.surface,
            borderRadius: radius.md,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          {terminaller.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: colors.textSecondary }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>💳</div>
              <div style={{ fontSize: 15, marginBottom: 4 }}>
                Henüz POS terminali tanımlanmamış.
              </div>
              <div style={{ fontSize: 13 }}>
                <strong>+ Yeni Terminal</strong> ile başlayın.
              </div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 10 }}>Kod</th>
                  <th style={{ padding: 10 }}>Ad</th>
                  <th style={{ padding: 10 }}>Banka</th>
                  <th style={{ padding: 10 }}>Lokasyon</th>
                  <th style={{ padding: 10 }}>Son 4</th>
                  <th style={{ padding: 10 }}>Sıra</th>
                  <th style={{ padding: 10 }}>Kullanım</th>
                  <th style={{ padding: 10 }}>Durum</th>
                  <th style={{ padding: 10, textAlign: 'right' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {terminaller.map((t) => {
                  const usage = t.usage_count || 0;
                  const lok = lokasyonMap[t.lokasyon_id];
                  const isAktif = t.aktif !== false;
                  return (
                    <tr
                      key={t.id}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        opacity: isAktif ? 1 : 0.55,
                        background: isAktif ? 'white' : '#fafafa',
                      }}
                    >
                      <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12 }}>
                        {t.kod}
                      </td>
                      <td style={{ padding: 10, fontWeight: 500 }}>{t.ad}</td>
                      <td style={{ padding: 10, color: '#555' }}>{t.banka || '—'}</td>
                      <td style={{ padding: 10 }}>
                        {lok ? lok.ad : <span style={{ color: '#999' }}>#{t.lokasyon_id}</span>}
                      </td>
                      <td style={{ padding: 10 }}>
                        <strong>{t.son_dort_hane || '—'}</strong>
                      </td>
                      <td style={{ padding: 10, color: '#666' }}>{t.sira ?? 0}</td>
                      <td style={{ padding: 10, fontSize: 11 }}>
                        {usage > 0 ? (
                          <span
                            style={{
                              background: '#fff3cd',
                              color: '#856404',
                              padding: '2px 8px',
                              borderRadius: 10,
                            }}
                          >
                            {usage} kapanış
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>kullanılmamış</span>
                        )}
                      </td>
                      <td style={{ padding: 10 }}>
                        <span
                          style={{
                            background: isAktif ? '#d4edda' : '#f8d7da',
                            color: isAktif ? '#155724' : '#721c24',
                            padding: '2px 8px',
                            borderRadius: radius.lg,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {isAktif ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => openEdit(t)}
                          title="Düzenle"
                          style={{
                            padding: '4px 10px',
                            background: '#0d6efd',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            marginRight: 4,
                          }}
                        >
                          ✏️ Düzenle
                        </button>
                        <button
                          onClick={() => togglePasif(t)}
                          title={isAktif ? 'Pasifleştir' : 'Aktifleştir'}
                          style={{
                            padding: '4px 10px',
                            background: isAktif ? '#ffc107' : '#28a745',
                            color: isAktif ? '#333' : 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            marginRight: 4,
                          }}
                        >
                          {isAktif ? 'Pasifleştir' : 'Aktifleştir'}
                        </button>
                        <button
                          onClick={() => hardDelete(t)}
                          disabled={usage > 0}
                          title={
                            usage > 0
                              ? `Bu terminal kapanışlarda kullanılıyor (${usage} kez). Önce kapanışları silin.`
                              : 'Kalıcı sil'
                          }
                          style={{
                            padding: '4px 10px',
                            background: usage > 0 ? '#ccc' : '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: usage > 0 ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                          }}
                        >
                          🗑️ Sil
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── Yeni Terminal Modal ─────────────────────────────────── */}
      {createModalOpen && (
        <div
          onClick={() => !saving && setCreateModalOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: radius.md,
              padding: 24,
              minWidth: 480,
              maxWidth: 560,
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>Yeni POS Terminali</h3>
            <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 16 }}>
              <strong>Kod</strong> sonradan değiştirilemez (referans bütünlüğü). Örn:
              <code style={{ marginLeft: 4 }}>TR_KASAP_GAR_1</code>
            </p>

            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
            >
              <div>
                <label style={labelStyle}>Kod*</label>
                <input
                  style={inputStyle}
                  value={createForm.kod}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, kod: e.target.value.toUpperCase() })
                  }
                  placeholder="TR_KASAP_GAR_1"
                  maxLength={50}
                  autoFocus
                />
              </div>
              <div>
                <label style={labelStyle}>Ad*</label>
                <input
                  style={inputStyle}
                  value={createForm.ad}
                  onChange={(e) => setCreateForm({ ...createForm, ad: e.target.value })}
                  placeholder="Toruna Garanti POS 1"
                  maxLength={200}
                />
              </div>
              <div>
                <label style={labelStyle}>Banka (ops.)</label>
                <input
                  style={inputStyle}
                  value={createForm.banka}
                  onChange={(e) => setCreateForm({ ...createForm, banka: e.target.value })}
                  placeholder="Garanti, Akbank, İş Bankası..."
                  maxLength={100}
                />
              </div>
              <div>
                <label style={labelStyle}>Lokasyon*</label>
                <select
                  style={inputStyle}
                  value={createForm.lokasyon_id}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, lokasyon_id: e.target.value })
                  }
                >
                  <option value="">— Seçiniz —</option>
                  {aktifLokasyonlar.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.ad}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Son 4 hane (ops.)</label>
                <input
                  style={inputStyle}
                  value={createForm.son_dort_hane}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      son_dort_hane: e.target.value.replace(/\D/g, '').slice(0, 4),
                    })
                  }
                  placeholder="8793"
                  maxLength={4}
                />
              </div>
              <div>
                <label style={labelStyle}>Sıra</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={createForm.sira}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, sira: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </div>
            </div>

            {createError && (
              <div
                style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  background: '#f8d7da',
                  color: '#721c24',
                  borderRadius: radius.sm,
                  fontSize: 13,
                }}
              >
                {createError}
              </div>
            )}

            <div
              style={{
                marginTop: 20,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
              }}
            >
              <button
                onClick={() => setCreateModalOpen(false)}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Vazgeç
              </button>
              <button
                onClick={submitCreate}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: saving ? '#999' : styles.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Düzenle Modal ───────────────────────────────────────── */}
      {editModal && (
        <div
          onClick={() => !saving && setEditModal(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: radius.md,
              padding: 24,
              minWidth: 480,
              maxWidth: 560,
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>POS Terminali Düzenle</h3>
            <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 16 }}>
              <strong>Kod</strong> kayıt oluşturulduktan sonra değiştirilemez (Anayasa
              Madde 3 — referans bütünlüğü).
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Kod (sabit)</label>
                <input
                  style={{
                    ...inputStyle,
                    background: '#f5f5f5',
                    fontFamily: 'monospace',
                    color: '#555',
                  }}
                  value={editModal.kod}
                  disabled
                  readOnly
                  title="Kod kayıt oluşturulduktan sonra değiştirilemez (Anayasa Madde 3)"
                />
              </div>
              <div>
                <label style={labelStyle}>Ad*</label>
                <input
                  style={inputStyle}
                  value={editModal.ad}
                  onChange={(e) => setEditModal({ ...editModal, ad: e.target.value })}
                  maxLength={200}
                />
              </div>
              <div>
                <label style={labelStyle}>Banka (ops.)</label>
                <input
                  style={inputStyle}
                  value={editModal.banka}
                  onChange={(e) => setEditModal({ ...editModal, banka: e.target.value })}
                  placeholder="Garanti, Akbank, İş Bankası..."
                  maxLength={100}
                />
              </div>
              <div>
                <label style={labelStyle}>Lokasyon*</label>
                <select
                  style={inputStyle}
                  value={editModal.lokasyon_id}
                  onChange={(e) =>
                    setEditModal({ ...editModal, lokasyon_id: e.target.value })
                  }
                >
                  <option value="">— Seçiniz —</option>
                  {lokasyonlar.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.ad} {l.aktif === false ? '(pasif)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Son 4 hane (ops.)</label>
                <input
                  style={inputStyle}
                  value={editModal.son_dort_hane}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      son_dort_hane: e.target.value.replace(/\D/g, '').slice(0, 4),
                    })
                  }
                  maxLength={4}
                />
              </div>
              <div>
                <label style={labelStyle}>Sıra</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={editModal.sira}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      sira: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label
                  style={{
                    ...labelStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={editModal.aktif}
                    onChange={(e) =>
                      setEditModal({ ...editModal, aktif: e.target.checked })
                    }
                  />
                  Aktif
                </label>
              </div>
            </div>

            {editError && (
              <div
                style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  background: '#f8d7da',
                  color: '#721c24',
                  borderRadius: radius.sm,
                  fontSize: 13,
                }}
              >
                {editError}
              </div>
            )}

            <div
              style={{
                marginTop: 20,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
              }}
            >
              <button
                onClick={() => setEditModal(null)}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Vazgeç
              </button>
              <button
                onClick={submitEdit}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: saving ? '#999' : styles.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosTerminalleriPage;
